import type { InventoryVehicle } from '@carvana/merch-contracts';
import MerchTile, {
  type FinanceData,
  type TileAvailabilityInfo,
  type TrackEvent,
  MoreDetailsModalSections
} from '@carvana/merch-tile';
import React, { useCallback, useEffect, useState } from 'react';
import { PRICE_DROP_SUPPRESSION_EXPERIMENT } from '~/constants/common/priceDropSuppression';
import { assetsCdnUrl, vexCdnUrl } from '~/constants/environment';
import useIsInPriceDropSuppressionExperiment from '~/hooks/common/experiments/useIsInPriceDropSuppressionExperiment';
import { useToggleFavorite } from '~/hooks/common/favorites';
import type { TileSharedProps } from '~/hooks/home/useTileSharedProps';
import { VehicleTileWrapper } from './VehicleTile.styles';

const ASSETS = {
  cvnaVexFastlyImageUrl: vexCdnUrl,
  cvnaAppAssetsCdn: assetsCdnUrl
};

type OwnProps = {
  availabilityInfo?: TileAvailabilityInfo;
  favoriteIds: number[];
  financeInfo?: FinanceData;
  hasBorder?: boolean;
  handleTileClick?: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => void;
  sectionTitle: string;
  sharedProps: TileSharedProps;
  toggleFavorite: ReturnType<typeof useToggleFavorite>;
  vehicle: InventoryVehicle;
};

function VehicleTile({
  availabilityInfo,
  favoriteIds,
  financeInfo,
  hasBorder = true,
  handleTileClick,
  sectionTitle,
  sharedProps,
  toggleFavorite,
  vehicle
}: OwnProps) {
  const {
    consumer,
    hasSoftPull,
    isFinanceRestricted,
    isLoggedIn,
    isTradeInApplied,
    isUserWithTrades,
    priceDropRestrictedStates,
    setMoreDetailsModalOpen,
    trackEvent,
    userLocationState
  } = sharedProps;

  const isInPriceDropSuppressionExperiment =
    useIsInPriceDropSuppressionExperiment();
  const [isFavoritedLocally, setFavoritedLocally] = useState(
    favoriteIds.includes(vehicle.vehicleId)
  );

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const { vehicleId, stockNumber } = vehicle;

      toggleFavorite({ vehicleId, stockNumber });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Vehicle Tile', {
        action: 'clicked',
        actionTaken: isFavoritedLocally ? 'removed' : 'added',
        description: `${sectionTitle} heart cta`,
        item: 'favorite heart',
        vehicleId
      });
    },
    [
      isFavoritedLocally,
      isLoggedIn,
      sectionTitle,
      toggleFavorite,
      trackEvent,
      vehicle
    ]
  );

  useEffect(() => {
    setFavoritedLocally(favoriteIds.includes(vehicle.vehicleId));
  }, [favoriteIds, vehicle.vehicleId]);

  return (
    <VehicleTileWrapper>
      <MerchTile
        assets={ASSETS}
        availabilityInfo={availabilityInfo}
        consumer={consumer}
        coreData={{
          ...vehicle,
          price: vehicle.price
            ? { ...vehicle.price, totalWithDiscounts: vehicle.price.total }
            : undefined
        }}
        experiments={{
          [PRICE_DROP_SUPPRESSION_EXPERIMENT]:
            isInPriceDropSuppressionExperiment ? 'treatment' : 'control'
        }}
        financeInfo={financeInfo}
        hasBorder={hasBorder}
        hasShadow={false}
        isFavorite={isFavoritedLocally}
        priceDropRestrictedStates={priceDropRestrictedStates}
        showFinanceInfo={!isFinanceRestricted}
        showTermsTooltip
        userInfo={{
          locationState: userLocationState,
          hasSoftPull,
          tradeInApplied: isUserWithTrades ? isTradeInApplied : false
        }}
        utils={{
          handleFavoriteClick: handleFavorite,
          handleTileClick,
          trackEvent: trackEvent as TrackEvent,
          openMoreDetailsModal: ({ openTo }) => {
            setMoreDetailsModalOpen(true, {
              vehicle,
              openTo: openTo ?? MoreDetailsModalSections.PriceDetails
            });
          }
        }}
      />
    </VehicleTileWrapper>
  );
}

export default React.memo(VehicleTile);
